import { SingleValueCardColor, SingleValueCardType } from "constants/index";

export type ColumnPeriodT = {
  name: string;
  period: [string, string];
};

export interface PerformanceComparisonTableProps {
  dateRange: [string | null, string | null];
  onColumnPeriodsChange: (periods: ColumnPeriodT[]) => void;
}

export interface PerformanceColumnSelectorModalProps {
  open: boolean;
  onClose: () => void;
  columnPeriods: ColumnPeriodT[];
  onChange: (columns: ColumnPeriodT[]) => void;
  dateRange: [string | null, string | null];
}

export interface StatisticState {
  dateRange: [string | null, string | null];
}

export interface IncomeSummaryProps {
  columnPeriods: ColumnPeriodT[];
}

export interface OtherStatisticProps {
  dateRange: [string | null, string | null];
  columnPeriods: ColumnPeriodT[];
}

// Report related interfaces
export interface ReportValue {
  date: string;
  beginning_value: number;
  value_change: number;
  cashflows: number;
  dividends: number;
  closing_price: number;
  nav_price: number;
  unit_balance: number;
  factor: number;
  stock_factor: number;
  time_weighted_return: number;
  time_weighted_return_stock: number;
}

export interface IncomeSummaryItem {
  name: string;
  value: ReportValue;
}

export interface GraphData {
  date: string;
  stock_value: number;
  option_value: number;
}

export interface ReportResponse {
  status: string;
  data: {
    report_data: string;
    income_summary: IncomeSummaryItem[];
    graph_data: GraphData[];
  };
  message: string;
}

export interface ReportParams {
  account_ids: number[];
  strategy_ids: number[];
  periods: {
    name: string;
    period: [string, string];
  }[];
}

/** PDF report request: matches backend PDFReportSerializer */
export type PDFReportType = "stock" | "strategy";

export interface PDFReportSettingsInfo {
  client_name: string;
  account: string;
  period: [string, string];
  symbol: string;
}

export interface PDFReportTableColumnRow {
  id: number;
  Period: string;
  "Stock Price": number;
  "Option Income": number;
  "Dividend Income": number;
  "Cumulative Income": number;
  Comment: string;
}

export interface PDFReportChartDataPoint {
  date: string;
  total: number;
}

export type PDFReportValueType =
  | "number"
  | "currency"
  | "percentage"
  | "shares";

export interface PDFReportClientInfoItem {
  label: string;
  value: string | number;
  type: PDFReportValueType;
  description: string;
}

export interface PDFReportValueItem {
  label: string;
  value: number;
  type: PDFReportValueType;
}

export interface PDFReportAddictionalComponent {
  type: "bar" | "donut";
  title: string;
  limit: number;
  data: PDFReportValueItem[];
}

export interface PDFReportThemeInfoItem {
  label: string;
  description: string;
  values: PDFReportValueItem[];
  addictional_component?: PDFReportAddictionalComponent | null;
}

export interface PDFReportRequest {
  type: PDFReportType;
  settings_info?: PDFReportSettingsInfo;
  table_columns?: PDFReportTableColumnRow[];
  chart_data?: PDFReportChartDataPoint[];
  client_info: PDFReportClientInfoItem[];
  theme_info?: PDFReportThemeInfoItem[];
}

// ALERT DOMAIN TYPES AND HELPERS
export type AlertLeg = {
  action?: string;
  expiration_date?: string;
  strike_price?: number | string | null;
};

export type AlertPayload = {
  contract?: {
    legs?: AlertLeg[];
  };
  [key: string]: unknown;
};

export type AlertType = {
  id: number;
  type?: string | null;
  status?: string | null;
  seen?: boolean;
  time_sent?: string | null;
  order_id?: string | null;
  symbol?: string | null;
  contract_type?: "C" | "P" | string | null;
  net_avg_price?: number | string | null;
  auto_extension_percentage?: number | null;
  auto_partial_close_percentage?: number | null;
  legs?: AlertLeg[];
  payload?: AlertPayload | string | null;
};

export const asText = (value: unknown) =>
  value === null || value === undefined ? "—" : String(value);

export const formatMoney = (value: unknown, decimals = 4) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `$${num.toFixed(decimals)}`;
};

export const contractLabel = (contractType?: string | null) => {
  if (contractType === "C") return "Call";
  if (contractType === "P") return "Put";
  return asText(contractType ?? "");
};

export enum PerformanceReportTypeEnum {
  STOCK = "stock",
  STRATEGY = "strategy",
}

export interface SingleValueCardProps {
  label: string;
  value: number;
  description: string;
  type: SingleValueCardType;
  color: SingleValueCardColor;
  onValueChange: (value: number) => void;
}
export interface MultiValuesCardProps {
  title: string;
  values: ReportValueCardProps[];
  description: string;
  additionalComponents: React.ReactNode | null;
}

export interface ReportValueCardProps {
  label: string;
  value: number | string;
  type: SingleValueCardType;
  onValueChange: (value: number | string) => void;
  fullWidthRow?: boolean;
  color?: SingleValueCardColor;
}

export interface ReportPdfResponse {
  download_url?: string;
  [key: string]: string[] | unknown;
}
